import { useState } from 'react';
import type { Dispatch } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import { DocumentCaptureZone } from '../../components/DocumentCaptureZone';
import type { DocVariant } from '../../components/CameraCapture';
import { RetryMessage } from '../../components/RetryMessage';
import { DOC_TYPE } from '../../types/kyc';
import type { DocType } from '../../types/kyc';
import type { RejectionReason } from '../../constants/rejectionMessages';
import type { KYCFlowState, KYCAction } from '../KYCFlowController';
import { uploadDocument, executeWorkflow, KYCApiError } from '../../services/kycApi';
import { useSimulation } from '../../contexts/SimulationContext';

interface CaptureIDStepProps {
  state: KYCFlowState;
  dispatch: Dispatch<KYCAction>;
}


// Paso de captura de documento de identidad: selector de tipo + zona de carga
export function CaptureIDStep({ state, dispatch }: CaptureIDStepProps) {
  const [selectedDocType, setSelectedDocType] = useState<DocType | null>(state.idDocType);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);

  // Configuración de simulación — si está activa se omiten las llamadas al backend
  const simulation = useSimulation();

  const handleSubmit = async () => {
    if (!selectedDocType) return;

    // En modo simulación se omiten upload y workflow — el stream simulado emite el resultado
    if (simulation.enabled) {
      dispatch({ type: 'SET_ID_DOC_TYPE', docType: selectedDocType });
      return;
    }

    const file = selectedFile;
    if (!file) return;

    setProcessing(true);
    dispatch({ type: 'SET_ERROR', message: '' });

    try {
      // Paso 1: subir el documento al doc-handler
      const uploadResponse = await uploadDocument({
        file,
        sessionId: state.session.sessionId,
        token: state.session.token,
      });

      // Paso 2a: guardar datos del anverso por si el backend detecta que es INE sin reverso.
      // SET_INE_FRONT_DATA no cambia el status — solo persiste filename y process_id en el estado.
      dispatch({
        type: 'SET_INE_FRONT_DATA',
        processId: uploadResponse.processId,
        filename: uploadResponse.filename,
      });

      // Paso 2b: guardar el filename como documento definitivo para la comparación facial.
      // Para pasaporte y para INE en un solo PDF este valor se mantiene hasta liveness.
      // Para INE con reverso separado, CaptureINEBackStep lo sobreescribe con el PDF fusionado.
      dispatch({
        type: 'SET_FINAL_DOC_FILENAME',
        filename: uploadResponse.filename,
      });

      // Paso 2c: registrar tipo de doc y pasar a VALIDATING_ID — el SSE emitirá el resultado
      dispatch({ type: 'SET_ID_DOC_TYPE', docType: selectedDocType });

      // Paso 3: disparar el pipeline de clasificación en el backend.
      // Se pasa el MIME type real del archivo para que el workflow sepa cómo procesarlo:
      // - PDF (application/pdf): puede contener ambas caras de la INE o solo una.
      // - Imagen (image/jpeg, image/png, etc.): siempre es una sola cara del documento.
      // Si el browser no reporta tipo, se usa application/octet-stream como fallback seguro.
      await executeWorkflow({
        action: 'classification',
        sessionId: state.session.sessionId,
        token: state.session.token,
        payload: {
          process_id: uploadResponse.processId,
          filename: uploadResponse.filename,
          content_type: file.type || 'application/octet-stream',
        },
      });

      // A partir de aquí el KYCFlowController recibe el resultado via SSE
    } catch (err) {
      // Revertir a CAPTURING_ID en caso de error de red o upload fallido
      dispatch({ type: 'RETRY_ID' });
      const message =
        err instanceof KYCApiError
          ? err.message
          : 'Ocurrió un error al procesar tu documento. Intenta de nuevo.';
      dispatch({ type: 'SET_ERROR', message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Encabezado */}
      <Box>
        <Typography variant="h5" gutterBottom>
          Verificación de identidad
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sube una foto clara de tu identificación oficial. Asegúrate de que sea legible y esté vigente.
        </Typography>
      </Box>

      {/* Alerta de error si el backend falló */}
      {state.errorMessage && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {state.errorMessage}
        </Alert>
      )}

      {/* Indicador de reintento */}
      {state.idRetries > 0 && (
        <Chip
          label={`Intento ${state.idRetries + 1} de 3`}
          color="warning"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      {/* Mensaje de instrucción si hubo rechazo previo */}
      {state.rejectionReason && state.idRetries > 0 && (
        <RetryMessage reason={state.rejectionReason as RejectionReason} />
      )}

      {/* Selector de tipo de documento */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          ¿Qué documento vas a presentar?
        </Typography>
        <Stack direction="row" spacing={2}>
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              border: selectedDocType === DOC_TYPE.INE ? '2px solid' : '1px solid',
              borderColor: selectedDocType === DOC_TYPE.INE ? 'primary.main' : 'divider',
            }}
          >
            <CardActionArea onClick={() => setSelectedDocType(DOC_TYPE.INE)} sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center', p: '0 !important' }}>
                <BadgeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle2">INE</Typography>
                <Typography variant="caption" color="text.secondary">
                  Credencial de Elector
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card
            variant="outlined"
            sx={{
              flex: 1,
              border: selectedDocType === DOC_TYPE.PASSPORT ? '2px solid' : '1px solid',
              borderColor: selectedDocType === DOC_TYPE.PASSPORT ? 'primary.main' : 'divider',
            }}
          >
            <CardActionArea onClick={() => setSelectedDocType(DOC_TYPE.PASSPORT)} sx={{ p: 2 }}>
              <CardContent sx={{ textAlign: 'center', p: '0 !important' }}>
                <ImportContactsIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle2">Pasaporte</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pasaporte mexicano vigente
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Stack>

        {/* Aviso: pasaporte o INE sin domicilio requieren comprobante adicional */}
        <Collapse in={selectedDocType === DOC_TYPE.PASSPORT || selectedDocType === DOC_TYPE.INE}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            {selectedDocType === DOC_TYPE.PASSPORT
              ? 'Con pasaporte se te pedirá un comprobante de domicilio adicional (recibo de luz, agua, teléfono o estado de cuenta, con antigüedad máxima de 3 meses).'
              : 'Si tu INE no incluye domicilio impreso, se te pedirá un comprobante de domicilio adicional.'}
          </Alert>
        </Collapse>
      </Box>

      {/* Zona de captura — solo visible si se seleccionó tipo de documento */}
      {selectedDocType && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Sube o fotografía tu documento
          </Typography>
          <DocumentCaptureZone
            hasFile={selectedFile !== null}
            onFileSelected={setSelectedFile}
            dropHint="Arrastra tu identificación aquí, o haz clic para seleccionar"
            cameraLabel={
              selectedDocType === DOC_TYPE.INE
                ? 'Coloca el anverso de tu INE dentro del marco'
                : 'Coloca tu pasaporte dentro del marco'
            }
            docVariant={
              (selectedDocType === DOC_TYPE.INE ? 'ine_front' : 'passport') as DocVariant
            }
          />
        </Box>
      )}

      {/* Botón de envío — muestra spinner mientras procesa */}
      <Button
        variant="contained"
        size="large"
        disabled={!selectedDocType || (!simulation.enabled && selectedFile === null) || processing}
        onClick={handleSubmit}
        fullWidth
        startIcon={processing ? <CircularProgress size={20} color="inherit" /> : undefined}
      >
        {processing ? 'Verificando...' : selectedFile ? 'Enviar para verificar' : 'Continuar'}
      </Button>
    </Box>
  );
}
